import React, { useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import MeetingPointResults from './pages/meeting-point-results';
import LocationInput from './pages/location-input';
import LocationTypeFilter from './pages/location-type-filter';
import MeetingConfirmation from './pages/meeting-confirmation';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import ActiveMeeting from './pages/active-meeting/index';
import NotificationSettings from './pages/notification-settings/index';
import HomeDashboard from './pages/home-dashboard/index';
import JoinMeeting from './pages/join-meeting';
import InvitationStatus from './pages/invitation-status';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { trackError, trackComponentError } from './utils/analytics';

const RoutesContent = () => {
  useGoogleAnalytics();

  // Set up global error tracking for ErrorBoundary
  useEffect(() => {
    if (!window.__GA_ERROR_TRACKING_SETUP__) {
      const originalComponentError = window.__COMPONENT_ERROR__;
      
      window.__COMPONENT_ERROR__ = (error, errorInfo) => {
        // Track to Google Analytics
        try {
          trackError(error, { 
            fatal: true,
            componentStack: errorInfo?.componentStack 
          });
          
          // Extract component name from error info if available
          const componentName = errorInfo?.componentStack?.split('\n')?.[1]?.trim() || 'Unknown';
          trackComponentError(componentName, error, errorInfo);
        } catch (trackingError) {
          console.error('Error tracking failed:', trackingError);
        }
        
        // Call original handler
        if (originalComponentError) {
          originalComponentError(error, errorInfo);
        }
      };
      
      window.__GA_ERROR_TRACKING_SETUP__ = true;
    }
  }, []);

  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      
      {/* Protected routes with error boundaries */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="HomeDashboard">
              <HomeDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/home-dashboard" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="HomeDashboard">
              <HomeDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/meeting-point-results" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="MeetingPointResults">
              <MeetingPointResults />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/location-input" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="LocationInput">
              <LocationInput />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/location-type-filter" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="LocationTypeFilter">
              <LocationTypeFilter />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/final-meeting-details" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="MeetingConfirmation">
              <MeetingConfirmation />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/active-meeting" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="ActiveMeeting">
              <ActiveMeeting />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notification-settings" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="NotificationSettings">
              <NotificationSettings />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/join-meeting" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="JoinMeeting">
              <JoinMeeting />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invitation-status" 
        element={
          <ProtectedRoute>
            <ErrorBoundary componentName="InvitationStatus">
              <InvitationStatus />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary componentName="App">
          <ScrollToTop />
          <RoutesContent />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
